import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import {
  Dialog,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  Box,
  IconButton,
  Slide,
  InputAdornment,
  useTheme,
  DialogContent,
  ListSubheader
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ClearIcon from '@mui/icons-material/Clear'
import CheckIcon from '@mui/icons-material/Check'
import { TransitionProps } from '@mui/material/transitions'
import { Unit } from '../types'
import { createUnitLabel } from '../utils/measureHelpers'
import { AppContext } from '../contexts/AppProvider'
import { toast } from 'react-toastify'
import { highlightSearchTerm } from './helpers/highlightSearchTerm'
import { useAppNavigate } from '../hooks/useAppNavigate'


interface UnitSearchMobileOverlayProps {
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  onOpenNewUnitRequestDialog: () => void
  placeholderLabel: string
  unit: Unit
  unitAutocompleteRef?: React.RefObject<HTMLInputElement>
}

const Transition = React.forwardRef(function Transition (
    props: TransitionProps & {
    children: React.ReactElement<unknown>
  },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />
})

const UnitSearchMobileOverlay: React.FC<UnitSearchMobileOverlayProps> = ({
  open,
  setOpen,
  onOpenNewUnitRequestDialog,
  placeholderLabel,
  unit,
  unitAutocompleteRef
}) => {
  const { navigateToConverter } = useAppNavigate()
  const theme = useTheme()
  const contentRef = useRef<HTMLDivElement>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [startY, setStartY] = useState<number | null>(null)

  const [currentY, setCurrentY] = useState<number | null>(null)
  const { appState } = useContext(AppContext)
  const { currentMeasure } = appState
  const unitsOfThisMeasure: { [unitName: string]: Unit } = currentMeasure.units
  const [shouldBlurUnitAutocomplete, setShouldBlurUnitAutocomplete] = useState(false)

  const filteredUnits = Object.values(unitsOfThisMeasure).filter(option =>
    createUnitLabel(option).toLowerCase().includes(searchTerm.toLowerCase())
  )


  useEffect(() => {
    // needed to blur TextField of autocomplete after rendering and also avoiding setTimeout
    if (shouldBlurUnitAutocomplete && unitAutocompleteRef?.current) {
      unitAutocompleteRef.current.blur()
      setShouldBlurUnitAutocomplete(false)
    }
  }, [shouldBlurUnitAutocomplete, unitAutocompleteRef])


  const handleListItemClick = (newUnit: Unit): void => {
    setOpen(false)
    setSearchTerm('')
    setShouldBlurUnitAutocomplete(true)

    let fromUnit: Unit = appState.tabs[currentMeasure.codeName].fromUnit
    let toUnit: Unit = appState.tabs[currentMeasure.codeName].toUnit

    if (placeholderLabel.toLowerCase().includes('from')) {
      if (newUnit === toUnit) {
        toUnit = fromUnit
        toast.info('Swapped Units')
      }
      fromUnit = newUnit
    } else if (placeholderLabel.toLowerCase().includes('to')) {
      if (newUnit === fromUnit) {
        fromUnit = toUnit
        toast.info('Swapped Units')
      }
      toUnit = newUnit
    }

    navigateToConverter(currentMeasure, fromUnit, toUnit)
  }

  const handleClearOverlaySearch = (): void => {
    setSearchTerm('')
  }

  const onClose = (): void => {
    setOpen(false)
  }

  const handleTouchStart = (event: React.TouchEvent): void => {
    setStartY(event.touches[0].clientY)
  }

  const handleTouchMove = (event: React.TouchEvent): void => {
    setCurrentY(event.touches[0].clientY)
  }

  const handleTouchEnd = (): void => {
    if (startY !== null && currentY !== null) {
      const swipeDistance = currentY - startY
      const swipeThreshold = 150 // Adjust this threshold as needed

      const isAtTop =
        (contentRef.current && contentRef.current.scrollTop <= 0) || contentRef.current?.scrollTop === undefined

      // If the swipe distance is positive (downward swipe) and exceeds the threshold, close the dialog
      if ((swipeDistance > swipeThreshold) && isAtTop) {
        onClose()
      }
    }

    // Reset the swipe positions
    setStartY(null)
    setCurrentY(null)
  }

  const handleRequestUnitClick = (): void => {
    setSearchTerm('')
    setOpen(false)
    onOpenNewUnitRequestDialog()
  }

  const groupedUnits = useMemo(() => {
    return filteredUnits.reduce<Record<string, Unit[]>>((acc, currentUnit) => {
      if (!acc[currentUnit.system]) {
        acc[currentUnit.system] = []
      }
      acc[currentUnit.system].push(currentUnit)
      return acc
    }, {})
  }, [filteredUnits])


  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      scroll='paper'
      TransitionComponent={Transition}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        padding="8px"
        sx={{
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)'
        }}>
        <IconButton onClick={onClose} sx={{ color: `${theme.palette.grey[500]}` }}>
          <ArrowBackIcon />
        </IconButton>
        <TextField
          placeholder={placeholderLabel}
          variant="outlined"
          size="small"
          fullWidth
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          InputProps={{
            endAdornment: searchTerm && ( // Show "X" icon only if there's input
              <InputAdornment position="end">
                <IconButton onClick={handleClearOverlaySearch} edge="end" size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
          sx={{
            '& .MuiOutlinedInput-input': {
              '&::placeholder': {
                color: `${theme.palette.grey[500]}`,
                opacity: 1
              }
            },
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                border: 'none'
              }
            }
          }}
        />
      </Box>

      {/* List of Options */}
      <DialogContent ref={contentRef} sx={{ padding: 0 }}>
        <List>
          {Object.entries(groupedUnits).map(([system, unitsInSystem]) => (
            <React.Fragment key={system}>
              <ListSubheader sx={{ bgcolor: theme.palette.background.paper }}>
                {system}
              </ListSubheader>
              {unitsInSystem.map(option => (
                <ListItem key={option.displayName} disablePadding>
                  <ListItemButton onClick={() => handleListItemClick(option)}>
                    <ListItemText
                      primary={
                        <Box display='flex' alignItems='center'>
                          <Box ml={1}
                            sx={option.displayName === unit.displayName
                              ? {
                                color: theme.palette.custom.solidBeauty,
                                fontWeight: 'bold'
                              }
                              : {}}>
                            {highlightSearchTerm(createUnitLabel(option), searchTerm)}
                          </Box>
                        </Box>
                      }
                    />
                    {option.displayName === unit.displayName && (
                      <ListItemIcon>
                        <CheckIcon sx={{ color: theme.palette.custom.solidBeauty }} />
                      </ListItemIcon>
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </React.Fragment>
          ))}

          {searchTerm !== '' && filteredUnits.length === 0 && (
            <ListItem disablePadding>
              <ListItemButton onClick={handleRequestUnitClick}>
                <ListItemText primary={highlightSearchTerm(`Request unit "${searchTerm}"`, searchTerm)} />
              </ListItemButton>
            </ListItem>
          )}
        </List>
      </DialogContent>
    </Dialog>
  )
}

export default UnitSearchMobileOverlay
